import { useEffect, useRef, useState } from 'react';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import {
  ehPdf,
  enviarComprovante,
  removerComprovante,
  urlComprovante,
} from '@/lib/comprovante';
import MenuAcoesFolha from '@/components/feature/MenuAcoesFolha';
import ModalExcluir from './ModalExcluir';

interface Props {
  eventoId: number;
  despesaId: number;
  comprovantePath: string | null;
  comprovanteNome: string | null;
  comprovanteTipo: string | null;
  aoMudar: () => void;
}

// Anexa, abre (URL assinada), troca e remove o comprovante de uma despesa.
export default function ComprovanteDespesa({
  eventoId,
  despesaId,
  comprovantePath,
  comprovanteNome,
  comprovanteTipo,
  aoMudar,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const botaoMenuRef = useRef<HTMLButtonElement>(null);

  const [enviando, setEnviando] = useState(false);
  const [abrindo, setAbrindo] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [menuAberto, setMenuAberto] = useState(false);
  const [verImagem, setVerImagem] = useState<string | null>(null);
  const [zoom, setZoom] = useState(false);
  const [confirmarRemover, setConfirmarRemover] = useState(false);
  const [removendo, setRemovendo] = useState(false);
  const [erroRemover, setErroRemover] = useState<string | null>(null);

  const tem = Boolean(comprovantePath);
  const pdf = ehPdf(comprovanteTipo) || (!comprovanteTipo && /\.pdf$/i.test(comprovanteNome ?? ''));

  useEffect(() => {
    if (!verImagem) return;
    const aoEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setVerImagem(null);
    };
    document.addEventListener('keydown', aoEsc);
    return () => document.removeEventListener('keydown', aoEsc);
  }, [verImagem]);

  async function escolherArquivo(e: React.ChangeEvent<HTMLInputElement>) {
    const arquivo = e.target.files?.[0];
    e.target.value = '';
    if (!arquivo) return;
    setEnviando(true);
    setErro(null);
    try {
      await enviarComprovante({ eventoId, despesaId, arquivo, pathAntigo: comprovantePath });
      aoMudar();
    } catch (err) {
      setErro(
        mensagemAmigavel(
          err,
          'Não foi possível enviar o comprovante. A despesa ficou salva; tente anexar de novo.',
        ),
      );
    } finally {
      setEnviando(false);
    }
  }

  async function abrir() {
    if (!comprovantePath) return;
    setAbrindo(true);
    setErro(null);
    try {
      const url = await urlComprovante(comprovantePath, 300);
      if (!url) throw new Error('Não foi possível abrir o comprovante.');
      if (pdf) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        setZoom(false);
        setVerImagem(url);
      }
    } catch (err) {
      setErro(mensagemAmigavel(err, 'Não foi possível abrir o comprovante. Tente de novo.'));
    } finally {
      setAbrindo(false);
    }
  }

  async function remover() {
    setRemovendo(true);
    setErroRemover(null);
    try {
      await removerComprovante(despesaId, comprovantePath);
      setConfirmarRemover(false);
      aoMudar();
    } catch (err) {
      setErroRemover(mensagemAmigavel(err, 'Não foi possível remover o comprovante. Tente de novo.'));
    } finally {
      setRemovendo(false);
    }
  }

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/*,application/pdf"
        className="hidden"
        onChange={escolherArquivo}
        aria-hidden="true"
        tabIndex={-1}
      />

      {!tem ? (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={enviando}
          className="inline-flex min-h-[44px] cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-secondary-300 bg-white px-3 py-1.5 text-xs font-medium text-foreground-800 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <i className={enviando ? 'ri-loader-4-line animate-spin' : 'ri-attachment-2'} />
          {enviando ? 'Enviando…' : 'Anexar comprovante'}
        </button>
      ) : (
        <div className="relative flex items-center gap-1">
          <button
            type="button"
            onClick={abrir}
            disabled={abrindo}
            className="inline-flex min-h-[44px] cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full bg-secondary-100 px-3 py-1.5 text-xs font-semibold text-secondary-700 transition-colors hover:bg-secondary-200 disabled:cursor-not-allowed disabled:opacity-60"
            title={comprovanteNome ?? 'Comprovante'}
          >
            <i
              className={
                abrindo
                  ? 'ri-loader-4-line animate-spin'
                  : pdf
                    ? 'ri-file-pdf-2-line'
                    : 'ri-image-line'
              }
            />
            Comprovante
          </button>

          <button
            type="button"
            ref={botaoMenuRef}
            onClick={() => setMenuAberto((v) => !v)}
            aria-label="Opções do comprovante"
            aria-expanded={menuAberto}
            aria-haspopup="menu"
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-secondary-400 transition-colors hover:bg-background-100 hover:text-foreground-800"
          >
            <i className="ri-more-2-fill" />
          </button>

          {menuAberto && (
            <MenuAcoesFolha
              aberto={menuAberto}
              aoFechar={() => setMenuAberto(false)}
              titulo={comprovanteNome ?? 'Comprovante'}
              ancora={botaoMenuRef}
            >
              <button
                type="button"
                onClick={() => {
                  setMenuAberto(false);
                  inputRef.current?.click();
                }}
                className="flex min-h-[44px] w-full cursor-pointer items-center gap-2 px-3 text-left text-sm text-foreground-800 transition-colors hover:bg-background-100"
              >
                <i className="ri-refresh-line" /> Trocar
              </button>
              <button
                type="button"
                onClick={() => {
                  setMenuAberto(false);
                  setErroRemover(null);
                  setConfirmarRemover(true);
                }}
                className="flex min-h-[44px] w-full cursor-pointer items-center gap-2 px-3 text-left text-sm text-danger-500 transition-colors hover:bg-background-100"
              >
                <i className="ri-delete-bin-6-line" /> Remover
              </button>
            </MenuAcoesFolha>
          )}
        </div>
      )}

      {erro && (
        <p className="mt-1 w-full text-xs text-danger-600" role="alert">
          {erro}
        </p>
      )}

      {verImagem && (
        <div
          className="fixed inset-0 z-[90] flex flex-col items-center justify-center bg-black/80 p-4"
          onClick={() => setVerImagem(null)}
        >
          <div className="mb-3 flex w-full max-w-3xl items-center justify-between">
            <p className="truncate text-sm font-medium text-white/90">
              {comprovanteNome ?? 'Comprovante'}
            </p>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setVerImagem(null);
              }}
              aria-label="Fechar"
              className="flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
            >
              <i className="ri-close-line text-2xl" />
            </button>
          </div>
          <div
            className={`max-h-[78vh] max-w-full overflow-auto rounded-lg bg-white ${
              zoom ? 'cursor-zoom-out' : 'cursor-zoom-in'
            }`}
            onClick={(e) => {
              e.stopPropagation();
              setZoom((z) => !z);
            }}
          >
            <img
              src={verImagem}
              alt={`Comprovante: ${comprovanteNome ?? 'despesa'}`}
              className={`block bg-white transition-transform ${
                zoom ? 'scale-150' : 'max-h-[78vh] max-w-full object-contain'
              }`}
            />
          </div>
          <p className="mt-3 text-xs text-white/70">
            {zoom ? 'Toque na imagem para reduzir' : 'Toque na imagem para ampliar'}
          </p>
        </div>
      )}

      {confirmarRemover && (
        <ModalExcluir
          titulo="Remover comprovante"
          mensagem="O comprovante desta despesa será apagado. A despesa continua salva."
          erro={erroRemover}
          salvando={removendo}
          aoConfirmar={() => void remover()}
          aoCancelar={() => setConfirmarRemover(false)}
        />
      )}
    </>
  );
}